import {
  collection,
  getDocs,
  DocumentData,
  deleteDoc,
  doc,
  setDoc,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../firebase';
import { handleAsyncError } from '../utils/errorHandling';

interface BackupData {
  timestamp: string;
  collections: {
    [collectionName: string]: DocumentData[];
  };
}

// Helper function to determine environment
function getEnvironment(): string {
  // Note: Create React App requires environment variables to be prefixed with REACT_APP_
  const env = process.env.REACT_APP_ENV;

  if (!env) {
    console.log('No environment variable found.');
    throw new Error('No environment variable found.');
  }
  /* 
  // TODO: Add fallback. Detect based on url maybe.
  else {
    // Fallback detection based on hostname or other environment indicators
    if (window.location.hostname.includes('alpha')) return 'alpha';
    if (window.location.hostname.includes('stage')) return 'stage';
    if (window.location.hostname.includes('prod')) return 'prod';
    return 'dev'; // default fallback
  }
  // */

  return env;
}

/**
 * Creates a backup of specified Firestore collections
 * @param collectionNames - Array of collection names to backup
 * @returns Object containing backup data
 */
export async function createFirestoreBackup(
  collectionNames: string[] = ['milestoneMaps', 'objectives'],
): Promise<BackupData> {
  try {
    const backup: BackupData = {
      timestamp: new Date().toISOString(),
      collections: {},
    };

    for (const collectionName of collectionNames) {
      const querySnapshot = await getDocs(collection(db, collectionName));
      backup.collections[collectionName] = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
    }

    return backup;
  } catch (error) {
    return handleAsyncError(error, 'createFirestoreBackup');
  }
}

/**
 * Downloads the backup as a JSON file
 * @param backup - Backup data to download
 */
export function downloadBackup(backup: BackupData): void {
  const json = JSON.stringify(backup, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const date = backup.timestamp.split('T')[0];
  const environment = getEnvironment();
  const filename = `firestore-backup-${date}.utc.${environment}.json`;

  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Creates and downloads a Firestore backup
 * @example
 * // In a component or utility function:
 * async function handleBackup() {
 *   try {
 *     const backup = await createFirestoreBackup();
 *     downloadBackup(backup);
 *   } catch (error) {
 *     console.error('Backup failed:', error);
 *   }
 * }
 */
export async function backupFirestore(): Promise<void> {
  try {
    const backup = await createFirestoreBackup();
    downloadBackup(backup);
  } catch (error) {
    handleAsyncError(error, 'backupFirestore');
  }
}

/**
 * Utility function to pretty print the backup data
 * @param backup - Backup data to print
 */
export function printBackupSummary(backup: BackupData): void {
  console.log('Backup Summary:');
  console.log('Timestamp:', backup.timestamp);
  console.log('Collections:');

  Object.entries(backup.collections).forEach(([collectionName, docs]) => {
    console.log(`  ${collectionName}: ${docs.length} documents`);
  });
}

/**
 * Deletes all documents in specified collections
 * @param collectionNames - Array of collection names to clear
 */
async function clearCollections(collectionNames: string[]): Promise<void> {
  for (const collectionName of collectionNames) {
    const querySnapshot = await getDocs(collection(db, collectionName));
    const deletePromises = querySnapshot.docs.map((doc) => deleteDoc(doc.ref));
    await Promise.all(deletePromises);
  }
}

/**
 * Restores backup by first clearing all collections, then inserting backup data
 * @param backup - Backup data to restore
 */
export async function restoreBackupWithClear(backup: BackupData): Promise<void> {
  try {
    const collectionNames = Object.keys(backup.collections);

    // First clear all collections
    await clearCollections(collectionNames);

    // Then restore the data
    for (const collectionName of collectionNames) {
      const collectionRef = collection(db, collectionName);
      const documents = backup.collections[collectionName];

      for (const document of documents) {
        const { id, ...data } = document;
        await setDoc(doc(collectionRef, id), data);
      }
    }
  } catch (error) {
    handleAsyncError(error, 'restoreBackupWithClear');
  }
}

/**
 * Upserts backup data into existing collections without clearing
 * @param backup - Backup data to upsert
 */
export async function upsertBackup(backup: BackupData): Promise<void> {
  try {
    for (const [collectionName, documents] of Object.entries(backup.collections)) {
      const collectionRef = collection(db, collectionName);

      for (const document of documents) {
        const { id, ...data } = document;
        // Existing fields not specified in data should be preserved rather than overwritten.
        // Use { merge: true } to preserve existing fields.
        await setDoc(doc(collectionRef, id), data, { merge: true });
      }
    }
  } catch (error) {
    handleAsyncError(error, 'upsertBackup');
  }
}

/**
 * Creates a backup of specified milestone maps (by uuid or shortId) and their objectives
 * @param mapIds - Array of milestone map UUIDs or shortIds to backup
 * @returns Object containing backup data
 */
export async function createSelectiveBackupForMaps(mapIds: string[]): Promise<BackupData> {
  try {
    const backup: BackupData = {
      timestamp: new Date().toISOString(),
      collections: {},
    };

    // 1. Fetch milestone maps by uuid or shortId (in batches of 10)
    const milestoneMaps: any[] = [];
    const seenMapUuids = new Set<string>();
    for (let i = 0; i < mapIds.length; i += 10) {
      const batch = mapIds.slice(i, i + 10);
      // Query for maps where uuid in batch OR shortId in batch
      // Firestore does not support 'or' queries directly, so we do two queries per batch
      // and merge results, deduplicating by uuid
      // 1. Query by uuid
      const qUuid = query(collection(db, 'milestoneMaps'), where('uuid', 'in', batch));
      const uuidSnapshot = await getDocs(qUuid);
      uuidSnapshot.forEach((docSnap) => {
        const data = { id: docSnap.id, ...docSnap.data() } as any;
        if (data && data.uuid && !seenMapUuids.has(data.uuid)) {
          milestoneMaps.push(data);
          seenMapUuids.add(data.uuid);
        }
      });
      // 2. Query by shortId
      const qShortId = query(collection(db, 'milestoneMaps'), where('shortId', 'in', batch));
      const shortIdSnapshot = await getDocs(qShortId);
      shortIdSnapshot.forEach((docSnap) => {
        const data = { id: docSnap.id, ...docSnap.data() } as any;
        if (data && data.uuid && !seenMapUuids.has(data.uuid)) {
          milestoneMaps.push(data);
          seenMapUuids.add(data.uuid);
        }
      });
    }
    backup.collections['milestoneMaps'] = milestoneMaps;

    // 2. Collect all objectiveUuids from the fetched maps
    const allObjectiveUuids = Array.from(
      new Set(
        milestoneMaps.flatMap((map) =>
          Array.isArray(map.objectiveUuids) ? map.objectiveUuids : [],
        ),
      ),
    );

    // 3. Fetch objectives by uuid (in batches of 10)
    const objectives: any[] = [];
    for (let i = 0; i < allObjectiveUuids.length; i += 10) {
      const batch = allObjectiveUuids.slice(i, i + 10);
      const q = query(collection(db, 'objectives'), where('uuid', 'in', batch));
      const querySnapshot = await getDocs(q);
      querySnapshot.forEach((doc) => {
        objectives.push({ id: doc.id, ...doc.data() });
      });
    }
    backup.collections['objectives'] = objectives;

    return backup;
  } catch (error) {
    return handleAsyncError(error, 'createSelectiveBackupForMaps');
  }
}
